import { useEffect, useState } from 'react';
import { Image, Input, Space, Switch, Table, Tag, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    DRAMA_SORT_DIRECTION_OPTIONS,
    DRAMA_SORT_OPTIONS,
    getAuditStatus,
    getDramaCover,
    getDramaTitle,
    resolveAssetUrl,
    statusIntlId,
    statusTagColor,
    type DramaListSortDirection,
    type DramaListSortField,
} from '@/pages/drama-review/utils';
import { SortDirectionSelect, SortFieldSelect } from '@/pages/drama-review/components/sort-filter';
import { isBannerDramaUnavailable } from '@/pages/client-banner/utils';

import type { DramaItem, DramaStatus } from '@/services/dramas';

type Props = {
    dramas: DramaItem[];
    selectedIds: Set<string>;
    loading?: boolean;
    disabled?: boolean;
    maxItems: number;
    selectedCount: number;
    total: number;
    page: number;
    pageSize: number;
    pageSizeOptions?: readonly number[];
    keyword: string;
    onlineOnly: boolean;
    sortField?: DramaListSortField;
    sortDirection: DramaListSortDirection;
    onPageChange: (page: number, pageSize: number) => void;
    onKeywordChange: (keyword: string) => void;
    onOnlineOnlyChange: (checked: boolean) => void;
    onSortFieldChange: (field?: DramaListSortField) => void;
    onSortDirectionChange: (direction: DramaListSortDirection) => void;
    onToggle: (drama: DramaItem, checked: boolean) => void;
};

const KEYWORD_DEBOUNCE_MS = 300;

const DramaPicker: React.FC<Props> = ({
    dramas,
    selectedIds,
    loading,
    disabled,
    maxItems,
    selectedCount,
    total,
    page,
    pageSize,
    pageSizeOptions = [10, 20, 50],
    keyword,
    onlineOnly,
    sortField,
    sortDirection,
    onPageChange,
    onKeywordChange,
    onOnlineOnlyChange,
    onSortFieldChange,
    onSortDirectionChange,
    onToggle,
}) => {
    const { formatMessage } = useIntl();
    const [keywordInput, setKeywordInput] = useState(keyword);
    const atMax = selectedCount >= maxItems;

    useEffect(() => {
        setKeywordInput(keyword);
    }, [keyword]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (keywordInput === keyword) return;
            onKeywordChange(keywordInput);
        }, KEYWORD_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [keyword, keywordInput, onKeywordChange]);

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap>
                <Input.Search
                    allowClear
                    value={keywordInput}
                    placeholder={formatMessage({ id: 'clientBanner.picker.search' })}
                    onChange={(event) => setKeywordInput(event.target.value)}
                    onSearch={(value) => {
                        setKeywordInput(value);
                        onKeywordChange(value);
                    }}
                    style={{ width: 240 }}
                />
                <SortFieldSelect
                    value={sortField}
                    placeholderId="dramaReview.sort.label"
                    options={DRAMA_SORT_OPTIONS}
                    onChange={onSortFieldChange}
                />
                <SortDirectionSelect
                    value={sortDirection}
                    disabled={!sortField}
                    options={DRAMA_SORT_DIRECTION_OPTIONS}
                    onChange={onSortDirectionChange}
                />
                <Space>
                    <Switch checked={onlineOnly} onChange={onOnlineOnlyChange} size="small" />
                    <Typography.Text>
                        <FormattedMessage id="clientBanner.picker.onlineOnly" />
                    </Typography.Text>
                </Space>
            </Space>
            <Table<DramaItem>
                bordered
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={dramas}
                pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: pageSizeOptions.map(String),
                    showTotal: (value) => formatMessage({ id: 'clientBanner.picker.total' }, { total: value }),
                    size: 'small',
                    onChange: onPageChange,
                }}
                scroll={{ y: 420 }}
                columns={[
                    {
                        title: <FormattedMessage id="clientBanner.picker.select" />,
                        width: 72,
                        align: 'center',
                        render: (_, record) => {
                            const selected = selectedIds.has(record.id);
                            const unavailable = isBannerDramaUnavailable(record);
                            return (
                                <Switch
                                    checked={selected}
                                    disabled={
                                        disabled ||
                                        (atMax && !selected) ||
                                        (unavailable && !selected)
                                    }
                                    size="small"
                                    onChange={(checked) => onToggle(record, checked)}
                                />
                            );
                        },
                    },
                    {
                        title: <FormattedMessage id="clientBanner.picker.cover" />,
                        width: 88,
                        render: (_, record) => {
                            const cover = resolveAssetUrl(getDramaCover(record));
                            return cover ? <Image src={cover} width={64} height={36} style={{ objectFit: 'cover' }} /> : '-';
                        },
                    },
                    {
                        title: <FormattedMessage id="clientBanner.picker.title" />,
                        ellipsis: true,
                        render: (_, record) => getDramaTitle(record),
                    },
                    {
                        title: <FormattedMessage id="clientBanner.picker.status" />,
                        width: 110,
                        render: (_, record) => {
                            const status = getAuditStatus(record) as DramaStatus;
                            return (
                                <Tag color={statusTagColor(status)}>
                                    {formatMessage({ id: statusIntlId(status) })}
                                </Tag>
                            );
                        },
                    },
                ]}
            />
        </Space>
    );
};

export default DramaPicker;
